import { fetchCIs, fetchCIById, saveCI, saveCIDraft, updateCIDraft } from '@/connectors/cis';

const SUPPORTED_OPERATIONS = {
  'scene.camera': {
    PUT: (draft, { name, values }) => ({
      ...draft,
      scene: { ...draft.scene, camera: { ...draft.scene?.camera, [name]: values } },
    }),
  },
  'init.pre': { PUT: (draft, { script }) => ({ ...draft, init: { ...draft.init, pre: script } }) },
  'init.post': { PUT: (draft, { script }) => ({ ...draft, init: { ...draft.init, post: script } }) },
  choices: {
    POST: (draft, { choice }) => ({ ...draft, choices: draft.choices ? [...draft.choices, choice] : [choice] }),
    PUT: (draft, { choice }) => {
      if (!draft.choices || !draft.choices.length) {
        return draft;
      }

      const choiceIndex = draft.choices.indexOf(draft.choices.find(({ name }) => name === choice.name));

      if (choiceIndex === -1) {
        return draft;
      }

      const updatedChoices = [...draft.choices];
      updatedChoices.splice(choiceIndex, 1, choice);

      return { ...draft, choices: updatedChoices };
    },
    DELETE: (draft, { choice: { name: choiceName } }) => {
      if (!draft.choices || !draft.choices.length) {
        return draft;
      }

      const choiceIndex = draft.choices.indexOf(draft.choices.find(({ name }) => name === choiceName));

      if (choiceIndex === -1) {
        return draft;
      }

      const updatedChoices = [...draft.choices];
      updatedChoices.splice(choiceIndex, 1);

      return { ...draft, choices: updatedChoices };
    },
  },
};

const initialState = () => ({
  resources: [],
  operations: JSON.parse(localStorage.getItem('previewerOperations')),
  filters: { limit: 50, offset: 0 },
});

const getters = {
  cItems: (state) => state.resources,
  cItemsFilters: (state) => state.filters,
  currentCItem: (state) => state.cItem,
  currentCIDraft: (state) => state.ciDraft,
  previewerOperations: (state) => (ciId) => state.operations?.[ciId],
};

const mutations = {
  setCItems(state, ci) {
    Object.assign(state, { resources: ci });
  },
  setCIFilters(state, filters) {
    Object.assign(state, { filters: { ...state.filters, ...filters } });
  },
  setCurrentCItem(state, ci) {
    Object.assign(state, {
      cItem: {
        ...ci,
        revisions: (ci.revisions?.sort(({ revision: a }, { revision: b }) => (a < b ? 1 : -1)).slice(0, 10) || []).map(
          ({ created_at: createdAt, ...revision }) => ({ ...revision, created_at: new Date(createdAt) }),
        ),
      },
    });
  },
  setPreviewerOperation(state, { operations, ciId }) {
    const currentOperations = JSON.parse(localStorage.getItem('previewerOperations'));

    const updatedDraft = operations.reduce(
      (acc, { component, operation, details }) => SUPPORTED_OPERATIONS[component][operation](acc, details),
      state.ciDraft,
    );

    Object.assign(state, {
      operations: {
        ...currentOperations,
        [ciId]: currentOperations?.[ciId]?.length ? currentOperations[ciId].concat(operations) : operations,
      },
      ciDraft: updatedDraft,
    });

    localStorage.setItem('previewerOperations', JSON.stringify(state.operations));
  },
  removePreviewerOperation(state, ciId) {
    const currentOperations = JSON.parse(localStorage.getItem('previewerOperations'));

    Object.assign(state, {
      operations: { ...currentOperations, [ciId]: undefined },
    });

    localStorage.setItem('previewerOperations', JSON.stringify(state.operations));
  },
  setCurrentCIDraft(state, draft) {
    Object.assign(state, {
      ciDraft: (state.operations?.[`${draft.namespace}:${draft.name}`] || [])?.reduce(
        (acc, { component, operation, details }) => SUPPORTED_OPERATIONS[component][operation](acc, details),
        draft,
      ),
    });
  },
};

const actions = {
  async fetchCItems({ state, rootState, commit }) {
    const response = await fetchCIs(state.filters, rootState.users.user.session.request);
    commit('setCItems', response.data.results);
    commit('setCIFilters', { total: response.data.total });
  },
  async fetchCItemById({ rootState, commit }, id) {
    const response = await fetchCIById({ id }, rootState.users.user.session.request);
    commit('setCurrentCItem', response.data);
    commit(
      'setCurrentCIDraft',
      response.data?.revisions?.reduce((prev, current) => (current.revision >= prev.revision ? current : prev), {
        revision: 0,
      }),
    );
  },
  createCItem({ rootState }, { namespace, name, description }) {
    return saveCI({ namespace, name, description }, { request: rootState.users.user.session.request });
  },
  createCIDraft({ rootState }, { namespace, name, description, models }) {
    return saveCIDraft({ namespace, name, description, models }, { request: rootState.users.user.session.request });
  },
  async updateCIDraft({ state, rootState, commit }, { description, ciId }) {
    const [namespace, name] = ciId.split(':');

    const response = await updateCIDraft(
      { namespace, name, description, operations: state.operations[ciId] },
      { request: rootState.users.user.session.request },
    );
    commit('setCurrentCIDraft', response.data);
    commit('removePreviewerOperation', ciId);
    return response;
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
